import { routeHref } from '../lib/links';
import { btnDemo, btnSales, btnTeam, INTENT_HREF, tlink } from './cta';
import { icon, type IconName } from './icons';
import { eyebrow, mesh } from './primitives';

export function foundationTiles(): string {
  const t: readonly (readonly [IconName, string, string])[] = [
    [
      'users',
      'Clients',
      'Every client, entity, property and parcel in one record, with engagement terms and history attached.',
    ],
    ['message', 'Communication', 'Email, portal messages and internal notes in one timeline per client and per case.'],
    [
      'database',
      'Data',
      'Assessments, values, deadlines, settlement activity and outcomes, structured across offices.',
    ],
    [
      'file',
      'Documents',
      'Notices, evidence, filings, agreements and invoices, linked to the right client, property or case.',
    ],
  ];
  return `<ul class="grid4" data-stagger>${t.map(([i, h, p], k) => `<li class="card lift rv"><span class="ic-tile">${icon(i)}</span><span class="idx">0${k + 1}</span><h3>${h}</h3><p>${p}</p></li>`).join('')}</ul>
  <div class="found-note rv"><span class="lab">One foundation</span><span>The shared foundation underneath every workflow.</span></div>`;
}

// Section 23 — six stages
const STAGES = [
  ['01', 'Discover', "Understand the organization's operations, teams, offices, markets, systems and challenges."],
  ['02', 'Map', 'Map clients, communication, data, documents, stages, hand-offs and approvals.'],
  ['03', 'Configure', 'Configure workflow stages, permissions, routing logic, business rules and approvals.'],
  ['04', 'Pilot', 'Run defined workflows with real users and agreed success criteria.'],
  ['05', 'Deploy', 'Move the configured workflow into production with appropriate enterprise controls.'],
  ['06', 'Expand', 'Add offices, jurisdictions, workflows, integrations and intelligence over time.'],
];
export function howWeWork({ full = true, head = true, id = '' } = {}): string {
  return `
  <section class="s" ${id ? `id="${id}"` : ''} aria-labelledby="hww-h">
    <div class="wrap">
      ${head ? `<div class="head rv">${eyebrow('How we work')}<h2 id="hww-h">Your workflow. Your methodology.</h2>${full ? "<p>We don't ask organizations to abandon the processes that make them successful. We learn how the organization operates, identify where technology can remove friction, and configure ReTax360 around that operating model.</p>" : ''}</div>` : ''}
      <ol class="stages rv ${full ? '' : 'compact'}" aria-label="Six stages">${STAGES.map(([n, h, p]) => `<li class="stg"><span class="wn">${n} · ${h.toUpperCase()}</span>${full ? `<p>${p}</p>` : ''}</li>`).join('')}</ol>
      <p class="closing rv">Configure. Don't conform.</p>
    </div>
  </section>`;
}

// Section 29 — operational impact
export function impactCards(): string {
  const c = [
    [
      'observed',
      'Observed',
      '30–60 minutes',
      'Time returned per team member per day',
      'In an enterprise deployment, ReTax360 has observed approximately 30–60 minutes of daily time savings per team member as workflows and information become easier to manage.',
      'ReTax360 operational observation',
    ],
    [
      'projected',
      'Projected',
      '3–4 months',
      'Potential document-intake time returned over a year',
      'Based on current workflow analysis, intelligent document intake is projected to eliminate the equivalent of approximately three to four months of cumulative manual document-processing effort over the course of a year.',
      'Projected operational impact based on current workflow analysis',
    ],
    [
      'target',
      'Target',
      '60–70%',
      'Manual intake reduction objective',
      'Current enterprise program objectives target a 60–70% reduction in manual intake processing through AI-assisted classification, routing and automated data extraction.',
      'Enterprise program target',
    ],
  ];
  return `
  <div class="head-row"><div class="head rv">${eyebrow('Operational impact')}<h2 id="impact-h">What better infrastructure can give back.</h2></div><div class="rv">${tlink('Estimate your team’s capacity', routeHref('/calculator'))}</div></div>
  <ul class="grid3" data-stagger>${c
    .map(
      ([k, tag, fig, t, p, l]) => `
    <li class="card impact ${k} rv"><span class="itag">${tag}</span><div class="fig">${fig}</div><h3>${t}</h3><p>${p}</p><div class="ilabel">${l}</div></li>`,
    )
    .join('')}</ul>`;
}
export function businessOutcome(): string {
  return `<div class="outcome-band rv"><h2 id="bo-h">Time returned to the work that matters.</h2><p>Every hour removed from repetitive intake, document handling, status checking and administrative hand-offs creates additional capacity for professionals to focus on analysis, client relationships, strategy and revenue-generating work. The goal isn't simply faster software. It's more productive professional capacity.</p></div>`;
}

// Section 28 — anonymized market insight card
export const INSIGHT_NOTE =
  "<!-- Anonymized market insights. Replace with attributed quotes ONLY with the person's and company's written permission. Never add names, titles tied to a named company, or logos without that permission. -->";
export const insightCard = (text: string, label: string): string =>
  `<figure class="insight rv"><span class="ieb">Market insight</span><blockquote>“${text}”</blockquote><figcaption>${label}</figcaption></figure>`;

// B13 — signature line bands
export function sigBand(line: string, { dark = false, founder = false, grad = '' } = {}): string {
  const body = grad ? line.replace(grad, `<span class="grad">${grad}</span>`) : line;
  return `
  <section class="sig ${dark ? 'dark' : ''}" aria-label="${founder ? 'Founder statement' : 'ReTax360 statement'}">
    <div class="wrap"><figure class="rv-fade">
      <blockquote>${founder ? '“' + body + '”' : body}</blockquote>
      <figcaption>${founder ? 'Asumani Sula, Co-Founder &amp; CEO, ReTax360' : 'RETAX360'}</figcaption>
    </figure></div>
  </section>`;
}

// Section 24/25 — North America
export function northAmerica({ compact = false } = {}): string {
  return `
  <section class="s ${compact ? 'tight-top' : ''}" id="north-america" aria-labelledby="na-h">
    <div class="wrap na">
      <div class="rv">
        ${eyebrow('North America · United States · Canada')}
        <h2 id="na-h" style="margin:16px 0">Built for property tax operations across North America.</h2>
        ${compact ? '' : '<p class="lede">ReTax360 is built to support property tax organizations across the United States and Canada, with workflows that can be configured around different jurisdictions, markets, offices and organizational structures. The architecture is not tied to a single state, province or jurisdiction. As organizations expand into new markets, ReTax360 is designed to evolve with their workflows, rules, data and operating requirements.</p>'}
        <p class="na-close">Serving organizations across North America. Designed to expand wherever our clients operate.</p>
        ${intlLine()}
        <div class="ctas" style="margin-top:24px">${btnTeam()}</div>
      </div>
      <div class="na-vis rv" aria-hidden="true"><div class="na-grid"></div><span class="na-l l1">United States</span><span class="na-l l2">Canada</span><span class="na-l l3">Configured per jurisdiction</span></div>
    </div>
  </section>`;
}
export const intlLine = (): string =>
  `<p class="intl">${icon('globe')}<span>Operating outside North America? <a href="${INTENT_HREF['enterprise-contact']}" data-cta="contact_enterprise_team">Talk to us about your requirements.</a></span></p>`;

export type ChecklistStatus = 'Supported' | 'Available' | 'On request' | '';

export type ChecklistRow = readonly [iconName: IconName, label: string, status?: ChecklistStatus, sub?: string];

function statusChip(s: ChecklistStatus | undefined): string {
  if (s === 'Supported' || s === 'Available') return `<span class="st avail">${s}</span>`;
  if (s === 'On request') return '<span class="st req">On request</span>';
  return '';
}
export function checklist(rows: readonly ChecklistRow[]): string {
  return `<ul class="cl-rows">${rows.map(([iconName, label, status, sub]) => `<li class="cl-row">${icon(iconName)}<span class="lbl">${label}${sub ? `<small>${sub}</small>` : ''}</span>${statusChip(status)}</li>`).join('')}</ul>`;
}

export function ctaBand({
  h = 'AI + Expertise. Built around how you work.',
  p = "See how ReTax360 can be configured around your organization's workflows, offices, markets and approval requirements.",
} = {}): string {
  return `
  <section class="cta dark" aria-labelledby="cta-h">
    ${mesh('soft')}
    <div class="wrap rv">
      ${eyebrow('Book an Enterprise Demo')}
      <h2 id="cta-h">${h}</h2>
      <p>${p}</p>
      <div class="ctas">${btnDemo()} ${btnSales()}</div>
    </div>
  </section>`;
}
